import type { FC } from "react";
import { Link } from "react-router-dom";
import type { MatchingResultItem } from "../../../../hooks/use-free-matching-api";

interface ScoreBarProps {
  label: string;
  score: number | null;
}

const ScoreBar: FC<ScoreBarProps> = ({ label, score }) => {
  if (score === null) return null;
  const pct = Math.round(score * 100);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-16 text-gray-500">{label}</span>
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-500 h-2 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-gray-600">{pct}%</span>
    </div>
  );
};

/**
 * @param {number} score - スコア値
 * @return {string} バッジCSSクラス
 */
function getScoreBadgeClass(score: number): string {
  if (score >= 0.8) return "badge-success";
  if (score >= 0.6) return "badge-warning";
  return "badge-error";
}

export interface MatchingResultCardProps {
  result: MatchingResultItem;
  onPropose: (result: MatchingResultItem) => void;
  onCompare: (result: MatchingResultItem) => void;
}

export const MatchingResultCard: FC<MatchingResultCardProps> = ({
  result,
  onPropose,
  onCompare,
}) => {
  const totalPct = Math.round(result.totalScore * 100);

  return (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
      {/* ヘッダー: 件名 + スコア */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <Link
            to={`/emails/search/${result.matchedEmailId}`}
            className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline truncate block"
          >
            {result.matchedSubject}
          </Link>
        </div>
        <span
          className={`badge shrink-0 ${getScoreBadgeClass(result.totalScore)}`}
        >
          {totalPct}%
        </span>
      </div>

      {/* 個別スコア */}
      <div className="space-y-1 mb-3">
        <ScoreBar label="スキル" score={result.skillScore} />
        <ScoreBar label="単価" score={result.rewardScore} />
        <ScoreBar label="勤務地" score={result.locationScore} />
        <ScoreBar label="稼働" score={result.availabilityScore} />
      </div>

      {/* AI 分析 */}
      {result.aiReasoning && (
        <div className="mb-3">
          <p className="text-xs text-gray-600">{result.aiReasoning}</p>
        </div>
      )}

      {/* ハイライト & リスク */}
      {(result.aiHighlights?.length || result.aiRisks?.length) && (
        <div className="flex flex-wrap gap-1 mb-3">
          {result.aiHighlights?.map(h => (
            <span
              key={h}
              className="badge badge-sm badge-success badge-outline"
            >
              {h}
            </span>
          ))}
          {result.aiRisks?.map(r => (
            <span
              key={r}
              className="badge badge-sm badge-warning badge-outline"
            >
              {r}
            </span>
          ))}
        </div>
      )}

      {/* アクションボタン */}
      <div className="flex justify-end gap-2">
        <button
          className="btn btn-xs btn-outline"
          onClick={() => onCompare(result)}
          disabled={!result.matchedEmailId}
          type="button"
        >
          詳細比較
        </button>
        {result.status === "proposed" ? (
          <span className="badge badge-sm badge-accent badge-outline">
            提案済み
          </span>
        ) : (
          <button
            className="btn btn-xs btn-outline btn-primary"
            onClick={() => onPropose(result)}
            type="button"
          >
            提案する
          </button>
        )}
      </div>
    </div>
  );
};
